// React and core dependencies
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
} from 'react';
import {useRecoilState, useRecoilValue} from 'recoil';
import {useNavigate, useLocation} from 'react-router-dom';
// Third-party libraries
import {IoClose} from 'react-icons/io5';
import {Capacitor} from '@capacitor/core';

// Custom hooks and utilities
import useLogger, {Action} from '@/hooks/useLogger';
// Constants
const SCROLL_DEBOUNCE_DELAY = 150; // 스크롤 이벤트 디바운싱 시간
import BrandListItem from '@/components/brandListItem';
import {IsOnlyBenefit, SelectedCategory, user} from '@/store';
import {ScrollPositions, scrollPositionsState} from '@/store/atoms';
import {ReactComponent as NewpickIcon} from '@/assets/icons/newpickIcon.svg';
import {ReactComponent as NewpickArrow} from '@/assets/icons/newpickArrow.svg';
import {ReactComponent as NPickIcon} from '@/assets/icons/nPick.svg';
import {ReactComponent as NoSearchResult} from '@/assets/icons/no-search-result-icon.svg';
import {ReactComponent as MapBtnIcon} from '@/assets/icons/map-btn-icon.svg';
import LocationLoader from '@/components/common/Loader/LocationLoader';
import {useScroll} from '@/hooks/useScroll';
import {openBrowserForLink} from '@/utils';

import styles from './styles.module.scss';

interface Props {
  items: BrandListItemType[];
  height: number;
  isMaxHeight: boolean;
  selectedBrand: BrandListItemType | undefined;
  goToMap: () => void;
  isScrollLock?: boolean;
  scrollRef: React.MutableRefObject<HTMLElement | null>;
  setSelectedBrand: React.Dispatch<
    React.SetStateAction<BrandListItemType | undefined>
  >;
  resetAllMarkers: () => void;
}

export default function BrandList({
  items,
  height,
  isMaxHeight,
  selectedBrand,
  goToMap,
  isScrollLock = false,
  scrollRef,
  setSelectedBrand,
  resetAllMarkers,
}: Props) {
  const navigator = useNavigate();
  const location = useLocation();
  const {resetScroll, setCombinedRef} = useScroll();
  const realtimeChecker = useRef<HTMLInputElement | null>(null);
  const accessToken = useRecoilValue(user);
  const [selectedCategory, setSelectedCategory] =
    useRecoilState(SelectedCategory);
  const [isOnlyBenefit, setIsOnlyBenefit] = useRecoilState(IsOnlyBenefit);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [scrollPosList, setScrollPosList] =
    useRecoilState(scrollPositionsState);
  const [isScrollRestoring, setIsScrollRestoring] = useState(false);
  const log = useLogger();

  const toggleSelectedCategory = (category: string) => {
    // 기존 카테고리와 같은 경우 무시 (중복 실행 방지)
    if (selectedCategory === category) {
      return;
    }

    // 카테고리 변경 후 스크롤 초기화
    setSelectedCategory(category);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  const filteredBrands = useMemo(() => {
    // 선택된 브랜드가 있는 경우, 해당 브랜드만 표시
    if (selectedBrand) {
      return items.filter(item => item.id === selectedBrand.id);
    }

    // 카테고리와 혜택 필터링 적용
    return items.filter(item => {
      const isInCategory =
        selectedCategory === 'All'
          ? true
          : item.categoryList.includes(selectedCategory);
      const hasBenefit = isOnlyBenefit ? item.state === 'contracted' : true;
      return isInCategory && hasBenefit;
    });
  }, [items, selectedCategory, isOnlyBenefit, selectedBrand]);

  // 스크롤 복원 로직
  useLayoutEffect(() => {
    if (!isScrollLock && items.length > 0 && scrollRef.current) {
      // 정규화된 경로
      const normalizedPath = '/map';

      // 저장된 스크롤 위치 확인
      const savedPosition = scrollPosList[normalizedPath];
      if (
        !savedPosition ||
        typeof savedPosition.y !== 'number' ||
        savedPosition.y <= 0
      ) {
        console.log('[복원] 저장된 스크롤 위치 없음');
        return;
      }

      // 컨텐츠를 즉시 숨김 처리
      setIsScrollRestoring(true);
      console.log('[복원] 스크롤 복원 시작 - 컨텐츠 숨김');

      // 저장된 스크롤 위치값
      const savedY = Math.round(savedPosition.y);
      console.log(`[복원] 복원할 위치: ${savedY}px`);

      // 초기화: 스크롤을 맨 위로 설정
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }

      // 실제 스크롤 복원 함수
      const executeRestore = () => {
        if (!scrollRef.current) return;

        // 컨테이너의 최대 스크롤 높이 계산
        const maxScrollTop =
          scrollRef.current.scrollHeight - scrollRef.current.clientHeight;

        // 스크롤 위치 제한 (최대값 초과 방지)
        const targetScrollTop = Math.min(
          savedY,
          maxScrollTop > 0 ? maxScrollTop : 0,
        );

        // 실제 스크롤 설정 (한 번에 적용)
        scrollRef.current.scrollTop = targetScrollTop;
        console.log(
          `[복원] 스크롤 위치 적용: ${targetScrollTop}px (최대: ${maxScrollTop}px)`,
        );

        // 스크롤 위치 확인 및 미세 조정
        const checkScrollPosition = () => {
          if (!scrollRef.current) return;

          const currentScrollTop = scrollRef.current.scrollTop;

          // 위치 차이가 있으면 다시 설정
          if (Math.abs(currentScrollTop - targetScrollTop) > 5) {
            scrollRef.current.scrollTop = targetScrollTop;
            console.log(
              `[복원] 스크롤 위치 재조정: ${currentScrollTop}px → ${targetScrollTop}px`,
            );
          } else {
            console.log(`[복원] 스크롤 위치 확인 완료: ${currentScrollTop}px`);
          }

          // 스크롤이 완료된 후 추가 지연 시간을 두고 컨텐츠 표시
          setTimeout(() => {
            console.log('[복원] 지연 시간 후 컨텐츠 표시 시작');
            setIsScrollRestoring(false);

            // 이미지 로딩 시간 확보를 위한 추가 작업
            setTimeout(() => {
              // 화면에 보이는 모든 이미지에 대한 로드 촉진
              if (scrollRef.current) {
                const visibleItems =
                  scrollRef.current.querySelectorAll('[data-id]');
                console.log(
                  '[복원] 현재 화면에 ' +
                    visibleItems.length +
                    '개 아이템 발견',
                );

                // 요소의 가시성 체크 강제 트리거
                scrollRef.current.scrollTop = scrollRef.current.scrollTop + 1;
                setTimeout(() => {
                  if (scrollRef.current) {
                    scrollRef.current.scrollTop = targetScrollTop;
                  }
                }, 50);
              }
            }, 100);
          }, 300);
        };

        // 스크롤 적용 후 위치 확인
        setTimeout(checkScrollPosition, 200);
      };

      // 페이지 콘텐츠 로드를 위해 지연 후 스크롤 복원 실행
      const timer = setTimeout(executeRestore, 400);

      return () => {
        clearTimeout(timer);
        setIsScrollRestoring(false);
      };
    }
  }, [location, isScrollLock, scrollPosList, scrollRef, items]);

  // BrandListItem 클릭 이벤트 핸들러 추가
  const handleBrandClick = useCallback(
    (item: BrandListItemType) => {
      console.log('BrandListItem 클릭됨:', item);
      log({
        _msg: 'BrandClick',
        _screen: window.location.pathname,
        _action: Action.CLICK,
        _keyword: item.id.toString(),
        _data: {to: item},
      });
      // 상세 페이지 이동 전 현재 스크롤 위치 저장
      if (scrollRef.current) {
        const scrollY = Math.round(scrollRef.current.scrollTop || 0);
        const scrollHeight = scrollRef.current.scrollHeight;
        const clientHeight = scrollRef.current.clientHeight;

        // 스크롤 위치 백분율 계산 (더 정확한 위치 복원을 위해)
        const scrollPercent = scrollHeight > 0 ? scrollY / scrollHeight : 0;

        // 현재 화면에 보이는 아이템 ID 찾기
        const visibleItemId = item.id;

        // 스크롤 위치 저장
        const currentPath = '/map';
        const scrollPosition = {
          x: 0,
          y: scrollY,
          scrollPercent: scrollPercent,
          viewportHeight: clientHeight,
          contentHeight: scrollHeight,
          visibleItemId,
          lastClickedItemId: item.id,
          timestamp: Date.now(),
        };

        // scrollPosList 상태 업데이트
        setScrollPosList((prev: ScrollPositions) => {
          const newState = {
            ...prev,
            [currentPath]: scrollPosition,
          };

          console.log(
            `[상세이동] 스크롤 위치 저장: ${scrollY}px (${(
              scrollPercent * 100
            ).toFixed(2)}%, 아이템 ID: ${item.id})`,
          );
          return newState;
        });
      }

      // 상세 페이지로 이동
      navigator(`/brand/${item.parentId}/${item.id}`, {
        state: {
          brand: item,
          prev: location.pathname.replace('/', ''),
          scrollEnabled: true,
        },
      });
    },
    [scrollRef, navigator, location.pathname, setScrollPosList],
  );

  // 스크롤 이벤트 수정 - 위치 저장 비활성화 (상세 페이지 이동 시에만 저장)
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.style.overflow = isScrollLock ? 'hidden' : 'auto';

      const handleScroll = () => {
        setIsScrolling(true);

        if (scrollingTimerRef.current) {
          clearTimeout(scrollingTimerRef.current);
        }

        // 디바운스 적용 - 스크롤 상태만 관리
        scrollingTimerRef.current = setTimeout(() => {
          setIsScrolling(false);
        }, SCROLL_DEBOUNCE_DELAY);
      };

      scrollRef.current.addEventListener('scroll', handleScroll);
      return () => {
        scrollRef.current?.removeEventListener('scroll', handleScroll);
        if (scrollingTimerRef.current) {
          clearTimeout(scrollingTimerRef.current);
        }
      };
    }
  }, [isScrollLock, scrollRef]);

  return (
    <div style={{position: 'relative'}}>
      {!isMaxHeight && !selectedBrand && (
        <div className={styles.tabWrapper}>
          {/* tab */}
          <button
            className={`${styles.tab} ${
              selectedCategory === 'All' ? styles.selected : ''
            }`}
            onClick={() => toggleSelectedCategory('All')}>
            전체
          </button>
          <button
            className={`${styles.tab} ${
              selectedCategory === 'Cafe' ? styles.selected : ''
            }`}
            onClick={() => toggleSelectedCategory('Cafe')}>
            카페
          </button>
          <button
            className={`${styles.tab} ${
              selectedCategory === 'Restaurant' ? styles.selected : ''
            }`}
            onClick={() => toggleSelectedCategory('Restaurant')}>
            음식점
          </button>
          <button
            className={`${styles.tab} ${
              selectedCategory === 'Bar / Pub' ? styles.selected : ''
            }`}
            onClick={() => toggleSelectedCategory('Bar / Pub')}>
            바/펍
          </button>
        </div>
      )}
      {isMaxHeight && (
        <button className={styles.newpick} onClick={() => navigator('/n-pick')}>
          <NewpickIcon />
          <span>뉴뉴픽 브랜드는 할인권 받아가세요</span>
          <NewpickArrow />
        </button>
      )}
      {selectedBrand && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            margin: '10px 20px 10px 0',
          }}>
          <button
            onClick={() => {
              resetAllMarkers();
              setSelectedBrand(undefined);
            }}>
            <IoClose size={24} color="#9F9CA3" />
          </button>
        </div>
      )}
      {/* 스크롤 컨테이너 */}
      <div
        className={styles.brandsContainer}
        style={{
          position: 'relative',
          height: `${height}px`,
        }}>
        <div
          className={styles.brands}
          ref={el => setCombinedRef(el, scrollRef)}
          style={{
            overflow: 'auto',
            height: '100%',
            opacity: isScrollRestoring ? 0 : 1,
            transition: 'opacity 0.8s ease',
            visibility: isScrollRestoring ? 'hidden' : 'visible',
            pointerEvents: isScrollRestoring ? 'none' : 'auto',
          }}>
          {!selectedBrand && (
            <div className={styles.benefits} style={{cursor: 'pointer'}}>
              <label className={styles.custom_checkbox}>
                <input
                  type="checkbox"
                  checked={isOnlyBenefit}
                  ref={realtimeChecker}
                  onChange={() => {
                    if (accessToken) {
                      setIsOnlyBenefit(!isOnlyBenefit);
                    }
                  }}
                />
                <span className={styles.checkmark}></span>
              </label>
              <span
                onClick={() => {
                  if (realtimeChecker.current) {
                    realtimeChecker.current.click();
                  }
                }}>
                <NPickIcon />
              </span>
            </div>
          )}
          {/* list */}
          {filteredBrands.length > 0 ? (
            filteredBrands.map((item, i) => {
              return (
                <BrandListItem
                  key={`brand_list_item_${item.id}`}
                  item={item}
                  itemStyle={{
                    margin: i === 0 ? '0 0 40px 0' : '40px 0',
                  }}
                  isCurrentLocation={false}
                  isDisabled={isScrolling || false}
                  // 상세 페이지 이동 시 위치 저장 핸들러 추가
                  customOnClick={() => handleBrandClick(item)}
                />
              );
            })
          ) : (
            <div className={styles.noListWrapper}>
              <div className={styles.noList}>
                <NoSearchResult />
                <div className={styles.noListMsg}>
                  이 지역에는 브랜드가 없습니다.
                  <br />
                  지도 위치를 변경해 보세요.
                </div>
                <button
                  className={styles.noListBtn}
                  onClick={() => navigator('/recommend-brand')}>
                  이 지역 브랜드 추천하기
                </button>
                <button
                  className={styles.registerBrand}
                  onClick={() =>
                    openBrowserForLink('https://www.knewnewbiz.co.kr/')
                  }>
                  내 브랜드 등록 문의{' '}
                  <span className={styles.registerArrow}></span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 스크롤 복원 중일 때 로딩 표시기 */}
        {isScrollRestoring && (
          <div
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              zIndex: 10,
              opacity: 1,
              transition: 'opacity 0.3s ease',
            }}>
            <LocationLoader isBlack={true} />
          </div>
        )}
      </div>

      {isMaxHeight && (
        <div
          className={styles.keywordSelectorWrapper}
          onClick={e => e.stopPropagation()}>
          <div className={styles.keywordSelector}>
            <button
              className={`${styles.keyword} ${styles.keyword_left} ${
                selectedCategory === 'All' ? styles.selected : ''
              }`}
              onClick={e => {
                e.stopPropagation();
                toggleSelectedCategory('All');
              }}>
              전체
            </button>
            <button
              className={`${styles.keyword} ${
                selectedCategory === 'Cafe' ? styles.selected : ''
              }`}
              onClick={e => {
                e.stopPropagation();
                toggleSelectedCategory('Cafe');
              }}>
              카페
            </button>
            <button
              className={`${styles.keyword} ${
                selectedCategory === 'Restaurant' ? styles.selected : ''
              }`}
              onClick={e => {
                e.stopPropagation();
                toggleSelectedCategory('Restaurant');
              }}>
              음식점
            </button>
            <button
              className={`${styles.keyword} ${styles.keyword_right} ${
                selectedCategory === 'Bar / Pub' ? styles.selected : ''
              }`}
              onClick={e => {
                e.stopPropagation();
                toggleSelectedCategory('Bar / Pub');
              }}>
              바/펍
            </button>
          </div>
          <button
            className={styles.mapBtn}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              console.log('Map button clicked');
              resetScroll();

              const platform = Capacitor.getPlatform();
              console.log('Platform detected:', platform);

              if (platform !== 'web') {
                setTimeout(() => {
                  console.log('Calling goToMap in mobile');
                  goToMap();
                }, 150);
              } else {
                goToMap();
              }
            }}>
            <MapBtnIcon />
          </button>
        </div>
      )}
    </div>
  );
}
